/**
 * ChauparGamePersistence — Auto-save and resume for Chaupar games
 *
 * Saves game state after each turn to localStorage.
 * Supports resuming saved games.
 */

import { ChauparGameState } from '../types';

export class ChauparGamePersistence {
  private storageKey = 'chaupar_saved_game';

  saveGame(state: ChauparGameState): void {
    try {
      const serialized = JSON.stringify(state);
      localStorage.setItem(this.storageKey, serialized);
    } catch {
      // Silently fail on storage errors
    }
  }

  loadGame(): ChauparGameState | null {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (!stored) return null;

      const state = JSON.parse(stored) as ChauparGameState;

      // Basic validation
      if (!state.pawns || !state.currentTurn || !state.gameStatus) {
        return null;
      }

      // Don't resume completed games
      if (state.gameStatus.type !== 'active') {
        this.clearSavedGame();
        return null;
      }

      return state;
    } catch {
      return null;
    }
  }

  hasSavedGame(): boolean {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (!stored) return false;

      const state = JSON.parse(stored) as ChauparGameState;
      return state.gameStatus?.type === 'active';
    } catch {
      return false;
    }
  }

  clearSavedGame(): void {
    try {
      localStorage.removeItem(this.storageKey);
    } catch {
      // Silently fail
    }
  }
}
